import { create } from 'zustand';
import { persist } from 'zustand/middleware';

/**
 * Notification interface for dashboard
 */
export interface DashboardNotification {
  id: string;
  message: string;
  time: string;
  type: 'info' | 'warning' | 'success' | 'error';
  isRead: boolean;
  createdAt: Date;
}

/**
 * Notifications State
 */
interface NotificationsState {
  notifications: DashboardNotification[];
  isLoading: boolean;
  error: string | null;
  lastFetched: number | null;

  // Actions
  setNotifications: (notifications: DashboardNotification[]) => void;
  addNotification: (notification: Omit<DashboardNotification, 'id' | 'createdAt' | 'isRead'>) => void;
  addRealtimeNotification: (notification: DashboardNotification) => void;
  markAsRead: (id: string) => void;
  markAllAsRead: () => void;
  deleteNotification: (id: string) => void;
  clearAll: () => void;

  // Async actions
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;

  // Selectors
  getUnreadCount: () => number;
  getRecentNotifications: (limit?: number) => DashboardNotification[];

  // Reset
  reset: () => void;
}

const initialState = {
  notifications: [],
  isLoading: false,
  error: null,
  lastFetched: null,
};

/**
 * Notifications Store
 * Manages dashboard notifications with persist
 */
export const useNotificationsStore = create<NotificationsState>()(
  persist(
    (set, get) => ({
      ...initialState,

      // Set notifications
      setNotifications: (notifications) =>
        set({
          notifications,
          lastFetched: Date.now(),
          error: null,
        }),

      // Add single notification (generates ID)
      addNotification: (notification) =>
        set((state) => ({
          notifications: [
            {
              ...notification,
              id: `notif-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
              isRead: false,
              createdAt: new Date(),
            },
            ...state.notifications,
          ],
        })),

      // Add notification from realtime (uses existing ID, avoids duplicates)
      addRealtimeNotification: (notification) =>
        set((state) => {
          // Check if notification already exists
          const exists = state.notifications.some((n) => n.id === notification.id);
          if (exists) {
            return state;
          }
          return {
            notifications: [notification, ...state.notifications],
          };
        }),

      // Mark as read
      markAsRead: (id) =>
        set((state) => ({
          notifications: state.notifications.map((notif) =>
            notif.id === id ? { ...notif, isRead: true } : notif
          ),
        })),

      // Mark all as read
      markAllAsRead: () =>
        set((state) => ({
          notifications: state.notifications.map((notif) => ({
            ...notif,
            isRead: true,
          })),
        })),

      // Delete notification
      deleteNotification: (id) =>
        set((state) => ({
          notifications: state.notifications.filter((notif) => notif.id !== id),
        })),

      // Clear all
      clearAll: () =>
        set({
          notifications: [],
        }),

      // Loading state
      setLoading: (loading) =>
        set({
          isLoading: loading,
        }),

      // Error state
      setError: (error) =>
        set({
          error,
          isLoading: false,
        }),

      // Get unread count
      getUnreadCount: () => {
        const { notifications } = get();
        return notifications.filter((n) => !n.isRead).length;
      },

      // Get recent notifications
      getRecentNotifications: (limit = 5) => {
        const { notifications } = get();
        return notifications
          .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
          .slice(0, limit);
      },

      // Reset to initial state
      reset: () => set(initialState),
    }),
    {
      name: 'dashboard-notifications',
      // Only persist notifications, not loading/error states
      partialize: (state) => ({
        notifications: state.notifications,
        lastFetched: state.lastFetched,
      }),
    }
  )
);

/**
 * Selectors
 */
export const selectNotifications = (state: NotificationsState) => state.notifications;
export const selectUnreadNotifications = (state: NotificationsState) =>
  state.notifications.filter((n) => !n.isRead);
export const selectIsLoading = (state: NotificationsState) => state.isLoading;
export const selectError = (state: NotificationsState) => state.error;
